import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { t } from '@fetmode/i18n';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { homePathForMode } from './home-path-for-mode.js';
import { OnboardingChoiceCard } from './OnboardingChoiceCard.js';
import { OnboardingDock } from './OnboardingDock.js';
import { scrollOnboardingToTop, useOnboardingScrollTop } from './onboarding-scroll.js';
import {
  ONBOARDING_TAG_OPTIONS,
  saveOnboardingTags,
  type OnboardingTagId,
} from './onboarding-tags.js';
import './OnboardingFlow.css';

function tagLabel(tag: OnboardingTagId): string {
  return t(`common.experience.tag.${tag}`);
}

export function TagsOnboardingPage(): ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  useOnboardingScrollTop();
  const [selected, setSelected] = useState<OnboardingTagId[]>([]);

  const homePath = useMemo(
    () => homePathForMode(user?.primaryExperienceMode ?? 'LIVE'),
    [user?.primaryExperienceMode],
  );

  const toggleTag = (tag: OnboardingTagId): void => {
    setSelected((current) =>
      current.includes(tag) ? current.filter((entry) => entry !== tag) : [...current, tag],
    );
  };

  const finish = (tags: readonly OnboardingTagId[]): void => {
    saveOnboardingTags({ tags, completed: true });
    scrollOnboardingToTop();
    void navigate(homePath, { replace: true });
  };

  return (
    <section className="onboarding-flow">
      <p className="onboarding-flow__eyebrow">{t('common.experience.tagsEyebrow')}</p>
      <h1 className="onboarding-flow__title">{t('common.experience.tagsTitle')}</h1>
      <p className="onboarding-flow__subtitle">{t('common.experience.tagsSubtitle')}</p>

      <ul className="onboarding-flow__grid">
        {ONBOARDING_TAG_OPTIONS.map((tag) => (
          <li key={tag}>
            <OnboardingChoiceCard
              imageSeed={`tag-${tag}`}
              label={tagLabel(tag)}
              selected={selected.includes(tag)}
              onClick={() => {
                toggleTag(tag);
              }}
            />
          </li>
        ))}
      </ul>

      <OnboardingDock
        selectedCount={selected.length}
        continueLabel={t('common.experience.tagsContinue')}
        onContinue={() => {
          finish(selected);
        }}
        onSkip={() => {
          finish([]);
        }}
      />
    </section>
  );
}
